import { useState, useMemo } from "react";
import { trpc } from "@/lib/trpc";
import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Clock, UserCheck, UserX, Calendar, AlertTriangle, CheckCircle, Trash2, Plus, ChevronLeft, ChevronRight, MapPin, Radio } from "lucide-react";

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function monthRange(yearMonth: string) {
  const [y, m] = yearMonth.split("-").map(Number);
  const last = new Date(y, m, 0).getDate();
  return { startDate: `${yearMonth}-01`, endDate: `${yearMonth}-${String(last).padStart(2, "0")}` };
}

function fmtTime(ts: number | null | undefined) {
  if (!ts) return "—";
  return new Date(ts).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

function fmtMinutes(min: number) {
  if (min <= 0) return "0min";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}h${m > 0 ? m + "min" : ""}` : `${m}min`;
}

export default function AdminAttendance() {
  const today = todayStr();
  const [tab, setTab] = useState("daily");
  const [selectedDate, setSelectedDate] = useState(today);
  const [selectedProfId, setSelectedProfId] = useState<number | undefined>();
  const [yearMonth, setYearMonth] = useState(today.slice(0, 7));

  // Modal ponto manual
  const [showClockModal, setShowClockModal] = useState(false);
  const [clockForm, setClockForm] = useState({
    professionalId: 0,
    workDate: today,
    clockInTime: "",
    clockOutTime: "",
    expectedStart: "09:00",
    expectedEnd: "18:00",
    notes: "",
  });

  // Modal falta
  const [showAbsenceModal, setShowAbsenceModal] = useState(false);
  const [absenceForm, setAbsenceForm] = useState({
    professionalId: 0,
    absenceDate: today,
    reason: "",
    justified: false,
    discountAmount: "",
    notes: "",
  });

  const utils = trpc.useUtils();

  // Queries
  const { data: professionals = [] } = trpc.professionals.listAdmin.useQuery(undefined, { retry: false });
  const { startDate, endDate } = monthRange(yearMonth);

  const { data: records = [], isLoading: loadingRecords } = trpc.attendance.listRecords.useQuery(
    { professionalId: selectedProfId, startDate: selectedDate, endDate: selectedDate },
    { enabled: tab === "daily", refetchInterval: 15_000, refetchIntervalInBackground: true }
  );

  const { data: absences = [], isLoading: loadingAbsences } = trpc.attendance.listAbsences.useQuery(
    { professionalId: selectedProfId, startDate, endDate },
    { enabled: tab === "absences" }
  );

  const { data: summary = [], isLoading: loadingSummary } = trpc.attendance.monthlySummary.useQuery(
    { yearMonth },
    { enabled: tab === "summary" }
  );

  // Mutations
  const clockInMut = trpc.attendance.adminClockIn.useMutation({
    onSuccess: () => { utils.attendance.listRecords.invalidate(); toast.success("Entrada registrada!"); setShowClockModal(false); },
    onError: (e) => toast.error(e.message),
  });
  const clockOutMut = trpc.attendance.adminClockOut.useMutation({
    onSuccess: () => { utils.attendance.listRecords.invalidate(); toast.success("Saída registrada!"); setShowClockModal(false); },
    onError: (e) => toast.error(e.message),
  });
  const upsertAbsenceMut = trpc.attendance.upsertAbsence.useMutation({
    onSuccess: () => { utils.attendance.listAbsences.invalidate(); utils.attendance.monthlySummary.invalidate(); toast.success("Falta registrada!"); setShowAbsenceModal(false); },
    onError: (e) => toast.error(e.message),
  });
  const deleteAbsenceMut = trpc.attendance.deleteAbsence.useMutation({
    onSuccess: () => { utils.attendance.listAbsences.invalidate(); utils.attendance.monthlySummary.invalidate(); toast.success("Falta removida!"); },
    onError: (e) => toast.error(e.message),
  });
  const deleteRecordMut = trpc.attendance.deleteRecord.useMutation({
    onSuccess: () => { utils.attendance.listRecords.invalidate(); toast.success("Registro removido!"); },
    onError: (e) => toast.error(e.message),
  });

  function timeToMs(dateStr: string, timeStr: string): number {
    const [h, m] = timeStr.split(":").map(Number);
    const d = new Date(dateStr + "T00:00:00");
    d.setHours(h, m, 0, 0);
    return d.getTime();
  }

  function handleSaveClock() {
    if (!clockForm.professionalId) return toast.error("Selecione o profissional");
    if (!clockForm.clockInTime && !clockForm.clockOutTime) return toast.error("Informe ao menos entrada ou saída");
    if (clockForm.clockInTime) {
      clockInMut.mutate({
        professionalId: clockForm.professionalId,
        workDate: clockForm.workDate,
        clockIn: timeToMs(clockForm.workDate, clockForm.clockInTime),
        expectedStart: clockForm.expectedStart || undefined,
        expectedEnd: clockForm.expectedEnd || undefined,
        notes: clockForm.notes || undefined,
      });
    }
    if (clockForm.clockOutTime) {
      clockOutMut.mutate({
        professionalId: clockForm.professionalId,
        workDate: clockForm.workDate,
        clockOut: timeToMs(clockForm.workDate, clockForm.clockOutTime),
        notes: clockForm.notes || undefined,
      });
    }
  }

  function handleSaveAbsence() {
    if (!absenceForm.professionalId) return toast.error("Selecione o profissional");
    upsertAbsenceMut.mutate({
      professionalId: absenceForm.professionalId,
      absenceDate: absenceForm.absenceDate,
      reason: absenceForm.reason || undefined,
      justified: absenceForm.justified,
      discountAmount: absenceForm.discountAmount ? Number(absenceForm.discountAmount) : undefined,
      notes: absenceForm.notes || undefined,
    });
  }

  function prevMonth() {
    const [y, m] = yearMonth.split("-").map(Number);
    const d = new Date(y, m - 2, 1);
    setYearMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  function nextMonth() {
    const [y, m] = yearMonth.split("-").map(Number);
    const d = new Date(y, m, 1);
    setYearMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }

  const profMap = useMemo(() => {
    const m: Record<number, string> = {};
    for (const p of professionals as any[]) m[p.id] = p.name;
    return m;
  }, [professionals]);
  const teamAttendanceSummary = useMemo(() => summary.reduce((total: { professionals: number; absences: number; lateMinutes: number; overtimeMinutes: number; discounts: number }, item: any) => ({
    professionals: total.professionals + 1,
    absences: total.absences + Number(item.absences ?? 0),
    lateMinutes: total.lateMinutes + Number(item.lateMinutes ?? 0),
    overtimeMinutes: total.overtimeMinutes + Number(item.overtimeMinutes ?? 0),
    discounts: total.discounts + Number(item.totalDiscount ?? 0),
  }), { professionals: 0, absences: 0, lateMinutes: 0, overtimeMinutes: 0, discounts: 0 }), [summary]);
  const liveAttendance = useMemo(() => {
    const active = (records as any[]).filter((record) => record.clockIn && !record.clockOut).length;
    const completed = (records as any[]).filter((record) => record.clockIn && record.clockOut).length;
    const locationCaptured = (records as any[]).filter((record) => record.clockInLocationStatus === "captured" || record.clockOutLocationStatus === "captured").length;
    return { active, completed, locationCaptured };
  }, [records]);

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Controle de Ponto</h1>
            <p className="text-muted-foreground text-sm">Gerencie entradas, saídas e faltas da equipe</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setClockForm({ ...clockForm, workDate: today }); setShowClockModal(true); }}>
              <Clock className="w-4 h-4 mr-2" /> Registrar Ponto
            </Button>
            <Button variant="outline" onClick={() => { setAbsenceForm({ ...absenceForm, absenceDate: today }); setShowAbsenceModal(true); }}>
              <UserX className="w-4 h-4 mr-2" /> Lançar Falta
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="daily"><Clock className="w-4 h-4 mr-1" />Ponto Diário</TabsTrigger>
            <TabsTrigger value="absences"><UserX className="w-4 h-4 mr-1" />Faltas</TabsTrigger>
            <TabsTrigger value="summary"><Calendar className="w-4 h-4 mr-1" />Resumo Mensal</TabsTrigger>
          </TabsList>

          {/* ── Ponto Diário ── */}
          <TabsContent value="daily" className="space-y-4">
            <div className="rounded-xl border border-primary/15 bg-primary/5 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="flex items-center gap-2 font-semibold"><Radio className="h-4 w-4 text-primary animate-pulse" /> Painel de ponto em tempo real</p>
                  <p className="mt-1 text-xs text-muted-foreground">Atualiza automaticamente a cada 15 segundos para a data selecionada.</p>
                </div>
                <Badge variant="outline">{liveAttendance.active} em serviço</Badge>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center text-sm">
                <div className="rounded-lg bg-background/80 p-2"><p className="text-xs text-muted-foreground">Em serviço</p><p className="font-bold text-green-700">{liveAttendance.active}</p></div>
                <div className="rounded-lg bg-background/80 p-2"><p className="text-xs text-muted-foreground">Jornada encerrada</p><p className="font-bold text-blue-700">{liveAttendance.completed}</p></div>
                <div className="rounded-lg bg-background/80 p-2"><p className="text-xs text-muted-foreground">GPS registrado</p><p className="font-bold text-primary">{liveAttendance.locationCaptured}</p></div>
              </div>
            </div>
            <div className="flex gap-3 items-end">
              <div>
                <Label>Data</Label>
                <Input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)} className="w-44" />
              </div>
              <div className="w-56">
                <Label>Profissional</Label>
                <Select value={selectedProfId?.toString() ?? "all"} onValueChange={v => setSelectedProfId(v === "all" ? undefined : Number(v))}>
                  <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {(professionals as any[]).map((p: any) => (
                      <SelectItem key={p.id} value={p.id.toString()}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {loadingRecords ? (
              <div className="text-center py-8 text-muted-foreground">Carregando...</div>
            ) : records.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-muted-foreground">Nenhum registro para esta data.</CardContent></Card>
            ) : (
              <div className="space-y-2">
                {records.map((r: any) => {
                  const workedMin = r.clockIn && r.clockOut ? Math.round((r.clockOut - r.clockIn) / 60000) : null;
                  return (
                    <Card key={r.id}>
                      <CardContent className="py-3 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                            {profMap[r.professionalId]?.[0] ?? "?"}
                          </div>
                          <div>
                            <div className="font-medium">{profMap[r.professionalId] ?? `#${r.professionalId}`}</div>
                            <div className="text-xs text-muted-foreground">{r.workDate}</div>
                          </div>
                        </div>
                        <div className="flex gap-6 text-sm">
                          <div className="text-center">
                            <div className="text-xs text-muted-foreground">Entrada</div>
                            <div className="font-medium text-green-600">{fmtTime(r.clockIn)}</div>
                            {r.expectedStart && <div className="text-xs text-muted-foreground">Prev: {r.expectedStart}</div>}
                          </div>
                          <div className="text-center">
                            <div className="text-xs text-muted-foreground">Saída</div>
                            <div className="font-medium text-red-500">{fmtTime(r.clockOut)}</div>
                            {r.expectedEnd && <div className="text-xs text-muted-foreground">Prev: {r.expectedEnd}</div>}
                          </div>
                          {workedMin !== null && (
                            <div className="text-center">
                              <div className="text-xs text-muted-foreground">Trabalhado</div>
                              <div className="font-medium">{fmtMinutes(workedMin)}</div>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {r.registeredByAdmin && <Badge variant="secondary" className="text-xs">Admin</Badge>}
                          {(r.clockInLocationStatus || r.clockOutLocationStatus) && (
                            <Badge variant="outline" className="gap-1 text-xs">
                              <MapPin className="h-3 w-3" />
                              {r.clockInLocationStatus === "captured" || r.clockOutLocationStatus === "captured" ? "GPS registrado" : "GPS indisponível"}
                            </Badge>
                          )}
                          {r.notes && <span className="text-xs text-muted-foreground max-w-32 truncate">{r.notes}</span>}
                          <Button size="icon" variant="ghost" className="text-destructive" onClick={() => deleteRecordMut.mutate({ id: r.id })}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ── Faltas ── */}
          <TabsContent value="absences" className="space-y-4">
            <div className="flex gap-3 items-end">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
                <span className="font-medium w-28 text-center">{yearMonth}</span>
                <Button variant="ghost" size="icon" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
              </div>
              <div className="w-56">
                <Label>Profissional</Label>
                <Select value={selectedProfId?.toString() ?? "all"} onValueChange={v => setSelectedProfId(v === "all" ? undefined : Number(v))}>
                  <SelectTrigger><SelectValue placeholder="Todos" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {(professionals as any[]).map((p: any) => (
                      <SelectItem key={p.id} value={p.id.toString()}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {loadingAbsences ? (
              <div className="text-center py-8 text-muted-foreground">Carregando...</div>
            ) : absences.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-muted-foreground">Nenhuma falta registrada neste período.</CardContent></Card>
            ) : (
              <div className="space-y-2">
                {absences.map((a: any) => (
                  <Card key={a.id}>
                    <CardContent className="py-3 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center ${a.justified ? "bg-yellow-100 text-yellow-700" : "bg-red-100 text-red-600"}`}>
                          {a.justified ? <AlertTriangle className="w-4 h-4" /> : <UserX className="w-4 h-4" />}
                        </div>
                        <div>
                          <div className="font-medium">{profMap[a.professionalId] ?? `#${a.professionalId}`}</div>
                          <div className="text-xs text-muted-foreground">{a.absenceDate}</div>
                        </div>
                      </div>
                      <div className="flex gap-4 text-sm">
                        <Badge variant={a.justified ? "outline" : "destructive"} className="text-xs">
                          {a.justified ? "Justificada" : "Injustificada"}
                        </Badge>
                        {a.reason && <span className="text-muted-foreground">{a.reason}</span>}
                        {a.discountAmount && (
                          <span className="text-red-500 font-medium">-R$ {Number(a.discountAmount).toFixed(2)}</span>
                        )}
                      </div>
                      <Button size="icon" variant="ghost" className="text-destructive" onClick={() => deleteAbsenceMut.mutate({ id: a.id })}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ── Resumo Mensal ── */}
          <TabsContent value="summary" className="space-y-4">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="icon" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
              <span className="font-medium w-28 text-center">{yearMonth}</span>
              <Button variant="ghost" size="icon" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
            </div>

            {!loadingSummary && summary.length > 0 && <div className="grid grid-cols-2 gap-3 md:grid-cols-5"><Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Equipe acompanhada</p><p className="mt-1 text-xl font-bold">{teamAttendanceSummary.professionals}</p></CardContent></Card><Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Faltas</p><p className={`mt-1 text-xl font-bold ${teamAttendanceSummary.absences > 0 ? "text-destructive" : ""}`}>{teamAttendanceSummary.absences}</p></CardContent></Card><Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Atrasos</p><p className="mt-1 text-xl font-bold text-amber-700">{fmtMinutes(teamAttendanceSummary.lateMinutes)}</p></CardContent></Card><Card><CardContent className="p-3"><p className="text-xs text-muted-foreground">Horas extras</p><p className="mt-1 text-xl font-bold text-blue-700">{fmtMinutes(teamAttendanceSummary.overtimeMinutes)}</p></CardContent></Card><Card className="col-span-2 md:col-span-1"><CardContent className="p-3"><p className="text-xs text-muted-foreground">Descontos por falta</p><p className="mt-1 text-xl font-bold text-destructive">R$ {teamAttendanceSummary.discounts.toFixed(2).replace(".", ",")}</p></CardContent></Card></div>}

            {loadingSummary ? (
              <div className="text-center py-8 text-muted-foreground">Carregando...</div>
            ) : summary.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-muted-foreground">Nenhum dado para este mês.</CardContent></Card>
            ) : (
              <div className="grid gap-3">
                {summary.map((s: any) => (
                  <Card key={s.professionalId}>
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="font-semibold">{profMap[s.professionalId] ?? `#${s.professionalId}`}</div>
                        <div className="flex gap-2">
                          {s.absences > 0 && (
                            <Badge variant="destructive" className="text-xs">{s.absences} falta{s.absences > 1 ? "s" : ""}</Badge>
                          )}
                          {s.justifiedAbsences > 0 && (
                            <Badge variant="outline" className="text-xs">{s.justifiedAbsences} justificada{s.justifiedAbsences > 1 ? "s" : ""}</Badge>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                        <div className="bg-muted/50 rounded p-2 text-center">
                          <div className="text-xs text-muted-foreground">Dias trabalhados</div>
                          <div className="font-bold text-lg">{s.daysWorked}</div>
                        </div>
                        <div className="bg-muted/50 rounded p-2 text-center">
                          <div className="text-xs text-muted-foreground">Horas totais</div>
                          <div className="font-bold text-lg">{fmtMinutes(s.totalMinutes)}</div>
                        </div>
                        <div className="bg-yellow-50 rounded p-2 text-center">
                          <div className="text-xs text-yellow-700">Atrasos</div>
                          <div className="font-bold text-lg text-yellow-700">{fmtMinutes(s.lateMinutes)}</div>
                        </div>
                        <div className="bg-blue-50 rounded p-2 text-center">
                          <div className="text-xs text-blue-700">Horas extras</div>
                          <div className="font-bold text-lg text-blue-700">{fmtMinutes(s.overtimeMinutes)}</div>
                        </div>
                        {s.totalDiscount > 0 && (
                          <div className="bg-red-50 rounded p-2 text-center">
                            <div className="text-xs text-red-600">Desconto faltas</div>
                            <div className="font-bold text-lg text-red-600">R$ {s.totalDiscount.toFixed(2)}</div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal: Registrar Ponto */}
      <Dialog open={showClockModal} onOpenChange={setShowClockModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Registrar Ponto</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Profissional *</Label>
              <Select value={clockForm.professionalId?.toString() ?? ""} onValueChange={v => setClockForm(f => ({ ...f, professionalId: Number(v) }))}>
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {(professionals as any[]).map((p: any) => (
                    <SelectItem key={p.id} value={p.id.toString()}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data</Label>
              <Input type="date" value={clockForm.workDate} onChange={e => setClockForm(f => ({ ...f, workDate: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Entrada</Label>
                <Input type="time" value={clockForm.clockInTime} onChange={e => setClockForm(f => ({ ...f, clockInTime: e.target.value }))} />
              </div>
              <div>
                <Label>Saída</Label>
                <Input type="time" value={clockForm.clockOutTime} onChange={e => setClockForm(f => ({ ...f, clockOutTime: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Horário previsto entrada</Label>
                <Input type="time" value={clockForm.expectedStart} onChange={e => setClockForm(f => ({ ...f, expectedStart: e.target.value }))} />
              </div>
              <div>
                <Label>Horário previsto saída</Label>
                <Input type="time" value={clockForm.expectedEnd} onChange={e => setClockForm(f => ({ ...f, expectedEnd: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label>Observações</Label>
              <Input value={clockForm.notes} onChange={e => setClockForm(f => ({ ...f, notes: e.target.value }))} placeholder="Opcional..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClockModal(false)}>Cancelar</Button>
            <Button onClick={handleSaveClock} disabled={clockInMut.isPending || clockOutMut.isPending}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Lançar Falta */}
      <Dialog open={showAbsenceModal} onOpenChange={setShowAbsenceModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Lançar Falta</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Profissional *</Label>
              <Select value={absenceForm.professionalId?.toString() ?? ""} onValueChange={v => setAbsenceForm(f => ({ ...f, professionalId: Number(v) }))}>
                <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>
                  {(professionals as any[]).map((p: any) => (
                    <SelectItem key={p.id} value={p.id.toString()}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data da falta</Label>
              <Input type="date" value={absenceForm.absenceDate} onChange={e => setAbsenceForm(f => ({ ...f, absenceDate: e.target.value }))} />
            </div>
            <div>
              <Label>Motivo</Label>
              <Input value={absenceForm.reason} onChange={e => setAbsenceForm(f => ({ ...f, reason: e.target.value }))} placeholder="Ex: Atestado médico, falta sem aviso..." />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={absenceForm.justified} onCheckedChange={v => setAbsenceForm(f => ({ ...f, justified: v }))} />
              <Label>Falta justificada</Label>
            </div>
            <div>
              <Label>Valor do desconto (R$)</Label>
              <Input type="number" step="0.01" value={absenceForm.discountAmount} onChange={e => setAbsenceForm(f => ({ ...f, discountAmount: e.target.value }))} placeholder="0,00 — deixe vazio para calcular automaticamente" />
            </div>
            <div>
              <Label>Observações</Label>
              <Input value={absenceForm.notes} onChange={e => setAbsenceForm(f => ({ ...f, notes: e.target.value }))} placeholder="Opcional..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAbsenceModal(false)}>Cancelar</Button>
            <Button onClick={handleSaveAbsence} disabled={upsertAbsenceMut.isPending}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
